// View-model types for the treasury fiat-transactions module.
// Produced by ../api/fiatTransactionsParser.ts. Consumers (containers,
// components, composables) bind to these — never to the wire DTOs in
// ../api/fiatTransactionsContracts.ts.

// ─── Enums (mirror BE) ───────────────────────────────────────────────────────

export enum FIAT_TRANSACTION_TYPE {
  CREDIT = 'credit',
  DEBIT = 'debit',
}

export type FiatTransactionType = `${FIAT_TRANSACTION_TYPE}`

export enum FIAT_TRANSACTION_REASON {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  INVESTMENT = 'investment',
  REFUND = 'refund',
  RENTAL_INCOME = 'rental_income',
  RECHARGE_REVERSAL = 'recharge_reversal',
  ORDER_REFUND = 'order_refund',
  WITHDRAWAL_REVERSAL = 'withdrawal_reversal',
  RENTAL_INCOME_REVERSAL = 'rental_income_reversal',
}

export type FiatTransactionReason = `${FIAT_TRANSACTION_REASON}`

export enum FIAT_TRANSACTION_REFERENCE_TYPE {
  RECHARGE_REQUEST = 'recharge_request',
  WITHDRAW_REQUEST = 'withdraw_request',
  INVESTOR_RENTAL_PAYOUT = 'investor_rental_payout',
  ORDER = 'order',
  RENTAL_PERIOD = 'rental_period',
  UNKNOWN = 'unknown',
}

export type FiatTransactionReferenceType = `${FIAT_TRANSACTION_REFERENCE_TYPE}`

// Synthetic status for the unified "all transactions" list.
// `completed` = a settled ledger row (money moved). `rejected` = a rejected
// treasury request (deposit / withdrawal / refund) that never produced a
// ledger row — surfaced here so operators see the full picture.
export enum FIAT_TRANSACTION_STATUS {
  COMPLETED = 'completed',
  REJECTED = 'rejected',
}

export type FiatTransactionStatus = `${FIAT_TRANSACTION_STATUS}`

export type FiatTransactionsSortOrder = 'asc' | 'desc'

// ─── Row (list response item) ────────────────────────────────────────────────

export interface FiatTransactionRow {
  id: string
  createdAt: string
  status: FiatTransactionStatus
  transactionType: FiatTransactionType
  transactionReason: FiatTransactionReason
  amount: string
  currencyCode: string
  balanceBefore: string | null
  balanceAfter: string | null
  referenceType: FiatTransactionReferenceType
  referenceId: string | null
  externalReferenceId: string | null
  idempotencyKey: string
  investorId: string
  investorName: string
  investorEmail: string
  treasuryAccountId: string | null
  treasuryAccountName: string | null
  treasuryAccountBankName: string | null
}

export interface FiatTransactionsListResponse {
  data: FiatTransactionRow[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface FiatTransactionDetailInvestor {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  kycStatus: string | null
}

export interface FiatTransactionDetailTreasuryBankAccount {
  id: string
  accountName: string
  bankName: string
  currencyCode: string
  iban: string | null
}

export interface FiatTransactionDetailRechargeSettlement {
  requestId: string
  status: string
  paymentReference: string | null
  approvedBankReference: string | null
  approvedAt: string | null
  approvedBy: string | null
  completedAt: string | null
  documentUrl: string | null
  rejectionReason: string | null
}

export interface FiatTransactionDetailWithdrawDestinationBank {
  bankName: string
  accountHolderName: string
  accountNumberMasked: string
  iban: string | null
}

export interface FiatTransactionDetailWithdrawSettlement {
  requestId: string
  status: string
  settlementReference: string | null
  settledAt: string | null
  approvedAt: string | null
  approvedBy: string | null
  rejectionReason: string | null
  destinationBankAccount: FiatTransactionDetailWithdrawDestinationBank
}

export interface FiatTransactionDetailSettlement {
  recharge: FiatTransactionDetailRechargeSettlement | null
  withdraw: FiatTransactionDetailWithdrawSettlement | null
}

export interface FiatTransactionDetail {
  id: string
  createdAt: string
  status: FiatTransactionStatus
  rejectionReason: string | null
  transactionType: FiatTransactionType
  transactionReason: FiatTransactionReason
  amount: string
  currencyCode: string
  balanceBefore: string | null
  balanceAfter: string | null
  referenceType: FiatTransactionReferenceType
  referenceId: string | null
  externalReferenceId: string | null
  idempotencyKey: string
  metadata: Record<string, unknown> | null
  investor: FiatTransactionDetailInvestor
  treasuryBankAccount: FiatTransactionDetailTreasuryBankAccount | null
  settlement: FiatTransactionDetailSettlement
}

// ─── Request params ──────────────────────────────────────────────────────────

export interface GetFiatTransactionsParams {
  page?: number
  limit?: number
  treasuryAccountId?: string
  investorId?: string
  investorBankAccountId?: string
  investorName?: string
  investorEmail?: string
  status?: FiatTransactionStatus
  referenceType?: FiatTransactionReferenceType
  transactionType?: FiatTransactionType
  transactionReason?: FiatTransactionReason
  fromDate?: string
  toDate?: string
  amountMin?: string
  amountMax?: string
  sortOrder?: FiatTransactionsSortOrder
}
